import React, { useState } from 'react';
import DataTable from 'react-data-table-component';
import { Button } from 'react-bootstrap';
import Icon from '../../../../components/Icon/Icon';
import RemoveAgentModal from '../../../../components/Modals/RemoveAgentModal';
import { whatsappAgentAPI } from '../../../../helpers/whatsappApiHandler';

// Helper function to format last active date
const formatLastActive = (dateString) => {
  if (!dateString || dateString === 'Never' || dateString === 'Pending' || dateString === 'Inactive') {
    return dateString;
  }

  try {
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const diffTime = today.getTime() - dateOnly.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      // Today - show time in HH:MM AM/PM format
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } else if (diffDays === 1) {
      return 'yesterday';
    } else if (diffDays > 1) {
      return `${diffDays} days ago`;
    } else {
      // Future date (shouldn't happen but handle gracefully)
      return date.toLocaleDateString('en-US');
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

const WhatsAppAgentsDataTable = ({ data, loadingData, onRefresh, accessToken }) => {
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [actionType, setActionType] = useState('remove'); // 'remove' or 'cancel'
  const [isProcessing, setIsProcessing] = useState(false);

  const sortedData = data ? [...data].sort((a, b) => {
    const aRole = a.role;
    const bRole = b.role;

    if (aRole === 'manager' && bRole !== 'manager') return -1;
    if (aRole !== 'manager' && bRole === 'manager') return 1;
    return 0;
  }) : [];

  // Access token is now passed as a prop

  // Handle remove agent or cancel invitation
  const handleAgentAction = (agent, action) => {
    setSelectedAgent(agent);
    setActionType(action);
    setShowModal(true);
  };

  // Handle modal confirmation
  const handleConfirmAction = async () => {
    if (!selectedAgent) return;

    setIsProcessing(true);

    try {
      let response;
      if (actionType === 'remove') {
        response = await whatsappAgentAPI.unassignAgent(selectedAgent.agent_id, accessToken);
      } else {
        response = await whatsappAgentAPI.cancelInvitation(selectedAgent.agent_id, accessToken);
      }

      if (response.success) {
        setShowModal(false);
        setSelectedAgent(null);
        // Refresh the data
        if (onRefresh) {
          onRefresh();
        }
      } else {
        console.error('Action failed:', response.message);
        // You might want to show an error message to the user here
      }
    } catch (error) {
      console.error('Error performing action:', error);
      // You might want to show an error message to the user here
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    if (!isProcessing) {
      setShowModal(false);
      setSelectedAgent(null);
    }
  };
  const columns = [
    {
      name: 'Name',
      sortable: true,
      selector: (row) => row.name,
      cell: (row) => (
        <div style={{
          fontFamily: 'Poppins, sans-serif',
          fontSize: '14px',
          fontWeight: '400',
          color: '#344054',
          lineHeight: '1.5em'
        }}>
          {row.name}
        </div>
      )
    },
    {
      name: 'Email',
      sortable: true,
      selector: (row) => row.email,
      cell: (row) => (
        <div style={{
          fontFamily: 'Poppins, sans-serif',
          fontSize: '14px',
          fontWeight: '400',
          color: '#344054',
          lineHeight: '1.5em'
        }}>
          {row.email}
        </div>
      )
    },
    {
      name: 'Last Active',
      sortable: true,
      selector: (row) => row.lastActive,
      cell: (row) => {
        const isPending = row.status === 'pending' || row.lastActive === 'Pending';
        const isInactive = row.status === 'inactive' || row.lastActive === 'Inactive';
        const formattedDate = formatLastActive(row.lastActive);

        return (
          <div style={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: '14px',
            fontWeight: '400',
            color: isPending ? '#F59E0B' : isInactive ? '#6B7280' : '#344054',
            lineHeight: '1.5em',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            {isPending && (
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#F59E0B'
              }} />
            )}
            {isInactive && (
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#6B7280'
              }} />
            )}
            {formattedDate}
          </div>
        );
      }
    },
    {
      name: 'Role',
      sortable: true,
      selector: (row) => row.role,
      cell: (row) => {
        return (
          <span style={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: '14px',
            fontWeight: '400',
            color: '#344054',
            lineHeight: '1.5em'
          }}>
            {row.role}
          </span>
        );
      }
    },
    {
      name: 'Action',
      width: '150px',
      cell: (row) => {
        const isPending = row.status === 'pending' || row.lastActive === 'Pending';

        return (
          <div className="d-flex justify-content-center align-items-center gap-2">
            {row.role != 'manager' && <Button
              variant="link"
              className="p-0"
              onClick={() => {
                if (isPending) {
                  handleAgentAction(row, 'cancel');
                } else {
                  handleAgentAction(row, 'remove');
                }
              }}
              title={isPending ? "Cancel Invitation" : "Remove Agent"}
              style={{ border: 'none', background: 'transparent' }}
            >
              <Icon
                name="delete"
                color="#737C8A"
                width={24}
                height={24}
              />
            </Button>}

            {/* Resend invitation button - only for pending */}
            {isPending && (
              <Button
                variant="link"
                className="p-0"
                onClick={() => {
                  // TODO: Implement resend invitation
                }}
                title="Resend Invitation"
                style={{ border: 'none', background: 'transparent' }}
              >
                <Icon
                  name="refresh"
                  color="#0BA9E0"
                  width={24}
                  height={24}
                />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  const customStyles = {
    table: {
      style: {
        width: '100%',
        maxWidth: '100%'
      }
    },
    tableWrapper: {
      style: {
        width: '100%',
        maxWidth: '100%',
        overflowX: 'auto'
      }
    },
    headRow: {
      style: {
        backgroundColor: '#F1F6FF',
        borderBottom: '1px solid #E5E7EB',
        fontFamily: 'Poppins, sans-serif',
        fontSize: '14px',
        fontWeight: '600',
        color: '#344054',
        textTransform: 'uppercase',
        letterSpacing: '2%',
        lineHeight: '1.5em'
      }
    },
    headCells: {
      style: {
        padding: '8px 16px',
        borderRight: '1px solid #E5E7EB'
      }
    },
    cells: {
      style: {
        padding: '16px',
        borderRight: '1px solid #E5E7EB',
        borderBottom: '1px solid #E5E7EB'
      }
    },
    rows: {
      style: {
        backgroundColor: '#FFFFFF',
        '&:hover': {
          backgroundColor: '#F9FAFB'
        }
      }
    }
  };

  return (
    <div className="h-100" style={{
      width: '100%',
      maxWidth: '100%',
      overflowX: 'auto'
    }}>
      <DataTable
        columns={columns}
        data={sortedData}
        progressPending={loadingData}
        pagination
        paginationComponentOptions={{
          rowsPerPageText: 'Items per page:',
        }}
        customStyles={customStyles}
        noDataComponent={
          <div className="text-center py-5">
            <p className="text-muted">No agents found</p>
          </div>
        }
      />

      {/* Remove Agent / Cancel Invitation Modal */}
      <RemoveAgentModal
        show={showModal}
        onHide={handleCloseModal}
        onConfirm={handleConfirmAction}
        agentName={selectedAgent?.name || ''}
        isLoading={isProcessing}
        actionType={actionType}
      />
    </div>
  );
};

export default WhatsAppAgentsDataTable;
